/* eslint-disable no-nested-ternary */
import {
  Form, FormActionsAlign, FormHandlers,
} from '@doveit/bricks';
import React from 'react';
import { jobApplicationAssigneeUsersOptions, jobApplicationHrUsersOptions, jobApplicationInterviewerUsersOptions } from '../../constants';
import AgentAutoComplete from '../../../containers/agent-autocomplete/AgentAutoComplete';
import { AgentStatus, JobApplicationType } from '../../../domain/types';
import { HRInterviewFormModel } from './types';
import { hrInterviewFormValidationSchema } from './HRInterviewForm.schema';
import FormGeoField from '../../../geo/components/form-geo-field/FormGeoField';
import { GEO_FORM_MODEL_DEFAULT_VALUES } from '../../../geo/components/form-geo-field/constants';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import { Agent } from '../../../providers/api/dtos';
import { boolToBooleanString } from '../../../utils/boolean-string/booleanString';

export const HR_INTERVIEW_FORM_LOAD_DATA_ERROR_MESSAGE = 'Dati non caricati.';

export const HR_INTERVIEW_FORM_DEFAULT_VALUES: HRInterviewFormModel = {
  supervisorEmail: '',
  assigneeEmail: '',
  date: '',
  time: '',
  note: '',
  interviewerEmail: '',
  extraParticipantEmail: '',
  isRemote: 'true',
  address: GEO_FORM_MODEL_DEFAULT_VALUES,
  isFranchising: 'false',
};

export interface HRInterviewFormProps extends React.AriaAttributes {
  initialValues?: Partial<HRInterviewFormModel>,
  applicationType: JobApplicationType,
  hiddenFields?: Partial<Record<keyof HRInterviewFormModel, boolean>>,
  loading?: boolean,
  disabled?: boolean,
  innerRef?: React.MutableRefObject<FormHandlers>,
  onSubmit: (values: HRInterviewFormModel) => void,
}

const HRInterviewForm: React.FC<HRInterviewFormProps> = ({
  initialValues = {},
  applicationType,
  loading = false,
  disabled = false,
  onSubmit,
  innerRef,
  hiddenFields,
  ...rest
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...HR_INTERVIEW_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);

  const { data: initialAssigneeAgent, isLoading: assigneeAgentLoading, error: assigneeAgentError } = useAgent(applicationType === JobApplicationType.SALES ? mergedInitialValues.assigneeEmail : undefined);
  const { data: initialInterviewerAgent, isLoading: interviewerAgentLoading, error: interviewerAgentError } = useAgent(applicationType === JobApplicationType.SALES ? mergedInitialValues.interviewerEmail : undefined);
  const [assigneeAgent, setAssigneeAgent] = React.useState<Agent | undefined>(initialAssigneeAgent);
  const [interviewerAgent, setInterviewerAgent] = React.useState<Agent | undefined>(initialInterviewerAgent);

  return (
    <Form
      initialValues={mergedInitialValues}
      validationSchema={hrInterviewFormValidationSchema(hiddenFields)}
      innerRef={innerRef}
      onSubmit={onSubmit}
      loading={loading}
      disabled={disabled}
      {...rest}
    >{({
      values, setFieldValue, errors, touched, setFieldTouched,
    }) => (
      <>
        {!hiddenFields?.supervisorEmail && (
          <Form.Item size={{ MD: 1 / 2 }}>
            <Form.Select
              name="supervisorEmail"
              label="Gestore HR"
              aria-label="Campo per specificare il gestore HR"
              options={jobApplicationHrUsersOptions}
              required
            />
          </Form.Item>
        )}

        {!hiddenFields?.assigneeEmail && (
          <Form.Item size={{ MD: 1 / 2 }}>
            {applicationType === JobApplicationType.SALES ? (
              <AgentAutoComplete
                label="Assegnatario"
                aria-label="Campo per specificare l'assegnatario"
                placeholder="Cerca per nome"
                value={assigneeAgent}
                onSuggestionSelected={(agent) => {
                  setFieldValue('assigneeEmail', agent?.email.work);
                  setFieldValue('isFranchising', boolToBooleanString(agent?.franchising) ?? 'false');
                  setAssigneeAgent(agent ?? undefined);
                }}
                status={AgentStatus.ACTIVE}
                onBlur={() => setFieldTouched('assigneeEmail', true)}
                error={assigneeAgentError
                  ? HR_INTERVIEW_FORM_LOAD_DATA_ERROR_MESSAGE
                  : (touched.assigneeEmail ? errors.assigneeEmail : undefined)}
                loading={assigneeAgentLoading}
                required
              />
            ) : (
              <Form.Select
                name="assigneeEmail"
                label="Assegnatario"
                aria-label="Campo per specificare l'assegnatario"
                options={jobApplicationAssigneeUsersOptions}
                required
              />
            )}
          </Form.Item>
        )}

        <Form.Fieldset legend="Dettagli">
          {!hiddenFields?.date && (
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.Input
                name="date"
                type="date"
                label="Data"
                aria-label="Campo per specificare la data in cui richiamare"
                required
              />
            </Form.Item>
          )}

          {!hiddenFields?.time && (
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.Input
                label="Ora"
                type="time"
                aria-label="Campo per specificare l'ora in cui richiamare"
                name="time"
                required
              />
            </Form.Item>
          )}

          {!hiddenFields?.interviewerEmail && (
            <Form.Item size={{ MD: 1 / 2 }}>
              {applicationType === JobApplicationType.SALES ? (
                <AgentAutoComplete
                  label="Esaminatore"
                  aria-label="Campo per selezionare l'agente esaminatore"
                  placeholder="Cerca per nome"
                  value={interviewerAgent}
                  error={interviewerAgentError
                    ? HR_INTERVIEW_FORM_LOAD_DATA_ERROR_MESSAGE
                    : (touched.interviewerEmail ? errors.interviewerEmail : undefined)}
                  onBlur={() => setFieldTouched('interviewerEmail', true)}
                  onSuggestionSelected={(agent) => {
                    setFieldValue('interviewerEmail', agent?.email.work);
                    setInterviewerAgent(agent ?? undefined);
                  }}
                  status={AgentStatus.ACTIVE}
                  loading={interviewerAgentLoading}
                  required
                />
              ) : (
                <Form.Select
                  name="interviewerEmail"
                  label="Esaminatore"
                  aria-label="Campo per selezionare l'agente esaminatore"
                  options={jobApplicationInterviewerUsersOptions}
                  required
                />
              )}
            </Form.Item>
          )}

          {!hiddenFields?.extraParticipantEmail && (
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.Select
                name="extraParticipantEmail"
                label="Includi HR"
                aria-label="Campo per specificare HR"
                options={jobApplicationHrUsersOptions}
              />
            </Form.Item>
          )}

          {!hiddenFields?.isRemote && (
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.YesOrNotButtons
                label="Da remoto?"
                name="isRemote"
                aria-label="Campo per specificare se il colloquio è da remoto"
                required
              />
            </Form.Item>
          )}

          {!hiddenFields?.address && values.isRemote === 'false' && (
            <Form.Item>
              <FormGeoField
                name="address"
                aria-label="Campo per specificare l'indirizzo del colloquio"
                required
                enableManualEntry
              />
            </Form.Item>
          )}

          {!hiddenFields?.note && (
            <Form.Item>
              <Form.TextArea
                name="note"
                aria-label="Campo per specificare le note"
                label="Note"
                rows={6}
              />
            </Form.Item>
          )}

        </Form.Fieldset>

        {!innerRef && (
          <Form.Actions align={FormActionsAlign.RIGHT}>
            <Form.Submit label="Salva" />
          </Form.Actions>
        )}
      </>
    )}
    </Form>
  );
};

export default HRInterviewForm;
